import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Subscription } from "@/types/subscription.ts";
import { Badge } from "@/components/ui/badge.tsx";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.tsx";
import { useTranslation } from "@/i18n";

const SubscriptionCard = ({
  subscription,
  onUnsubscribe,
  onPayment,
}: {
  subscription: Subscription;
  onUnsubscribe: (id: number) => void;
  onPayment: (subscription: Subscription) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>
          {t("subscriptions.cardTitle", {
            count: subscription.ticket_count,
            lottery: subscription.lottery_name,
          })}
        </CardTitle>
        {subscription.status === "active" && (
          <Badge className="bg-green-600">
            {t("subscriptions.statusActive")}
          </Badge>
        )}
        {subscription.status === "payment" && (
          <Badge className="bg-yellow-600">
            {t("subscriptions.statusPayment")}
          </Badge>
        )}
        {subscription.status === "inactive" && (
          <Badge className="bg-red-600">
            {t("subscriptions.statusInactive")}
          </Badge>
        )}
      </CardHeader>
      <Separator />

      <CardContent className="flex flex-col gap-4">
        <div className="flex gap-2">
          <span>{t("subscriptions.subscribedAt")}</span>
          <span>{new Date(subscription.created_at).toLocaleString()}</span>
        </div>
        {subscription.status === "inactive" && (
          <div className="flex gap-2">
            <span>{t("subscriptions.unsubscribedAt")}</span>
            <span>{new Date(subscription.end_date!).toLocaleString()}</span>
          </div>
        )}
      </CardContent>
      <CardFooter>
        {subscription.status === "active" && (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="destructiveOutline" className="ml-auto">
                {t("subscriptions.unsubscribe")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {t("subscriptions.unsubscribeConfirmTitle")}
                </DialogTitle>
                <DialogDescription>
                  {t("subscriptions.unsubscribeConfirmDescription")}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="submit" variant="outline">
                    {t("common.cancel")}
                  </Button>
                </DialogClose>
                <DialogClose asChild>
                  <Button
                    type="submit"
                    variant="destructive"
                    onClick={() => onUnsubscribe(subscription.id)}
                  >
                    {t("subscriptions.unsubscribe")}
                  </Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {subscription.status === "payment" && (
          <Button onClick={() => onPayment(subscription)} className="ml-auto">
            {t("subscriptions.goToPayment")}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default SubscriptionCard;
